import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import ConfirmDialog from './ui/ConfirmDialog';
import apiService from '../services/ApiService';
import CustomDropdown from './CustomDropdown';
import { useToast } from '../contexts/ToastContext';

type SettingSource = 'database' | 'env' | 'default' | 'none';

interface ScalarSetting {
  value?: boolean | string | number;
  source?: SettingSource;
}

interface SecretSetting {
  configured?: boolean;
  preview?: string;
  source?: SettingSource;
}

interface AiSettingsSnapshot {
  _meta?: {
    provider: string;
    table_ready: boolean;
    effective_model: string;
    ai_configured: boolean;
  };
  [key: string]: ScalarSetting | SecretSetting | AiSettingsSnapshot['_meta'] | undefined;
}

type FormValues = Record<string, string | boolean | number>;

const PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'cohere', label: 'Cohere' },
  { value: 'local', label: 'Local / self-hosted' },
  { value: 'rule-based', label: 'Rule-based (no AI)' },
];

const ASSESSMENT_MODES = [
  { value: 'inline', label: 'Inline (same request)' },
  { value: 'background', label: 'Background worker' },
  { value: 'auto', label: 'Auto-detect' },
];

const SECRET_KEYS = ['openai_api_key', 'anthropic_api_key', 'cohere_api_key', 'local_ai_api_key'];

function sourceBadge(source?: SettingSource) {
  const styles: Record<SettingSource, string> = {
    database: 'bg-orange-900/40 text-orange-200 border-orange-700/60',
    env: 'bg-blue-900/60 text-blue-200 border-blue-700',
    default: 'bg-[#161616] text-gray-300 border-white/[0.10]',
    none: 'bg-red-900/40 text-red-200 border-red-800',
  };
  const label = source ?? 'default';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border ${styles[label] ?? styles.default}`}>
      {label}
    </span>
  );
}

function Toggle({
  label,
  description,
  checked,
  onChange,
  source,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  source?: SettingSource;
}) {
  return (
    <label className="flex items-start justify-between gap-4 py-3 border-b border-white/[0.08] last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white">{label}</span>
          {source && sourceBadge(source)}
        </div>
        {description && <p className="text-xs text-gray-400 mt-1">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
          checked ? 'bg-orange-600' : 'bg-[#1a1a1a]'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </label>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  source,
  hint,
}: {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'password';
  placeholder?: string;
  source?: SettingSource;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm font-medium text-white">{label}</label>
        {source && sourceBadge(source)}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.10] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

function SecretField({
  label,
  preview,
  configured,
  source,
  value,
  onChange,
  onClear,
  clearing,
}: {
  label: string;
  preview?: string;
  configured?: boolean;
  source?: SettingSource;
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  clearing: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm font-medium text-white">{label}</label>
        {source && sourceBadge(source)}
        {configured && (
          <span className="text-xs text-green-400">Configured {preview ? `(${preview})` : ''}</span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          type="password"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter new key to replace, or leave blank to keep current"
          autoComplete="new-password"
          className="flex-1 px-3 py-2 bg-[#0a0a0a] border border-white/[0.10] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        {source === 'database' && (
          <button
            type="button"
            onClick={onClear}
            disabled={clearing}
            className="px-3 py-2 text-sm rounded-lg border border-white/[0.10] text-gray-300 hover:bg-white/[0.06] disabled:opacity-50"
          >
            {clearing ? '…' : 'Clear override'}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Values stored in the database are encrypted. Empty save keeps the existing key.
      </p>
    </div>
  );
}

export const AiSettingsManagement: React.FC = () => {
  const { showToast } = useToast();
  const [snapshot, setSnapshot] = useState<AiSettingsSnapshot | null>(null);
  const [form, setForm] = useState<FormValues>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearingKey, setClearingKey] = useState<string | null>(null);
  const [pendingClearKey, setPendingClearKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAdminAiSettings();
      setSnapshot(data);
      initializeForm(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load AI settings';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeForm = (data: AiSettingsSnapshot) => {
    const next: FormValues = {};
    Object.entries(data).forEach(([key, entry]) => {
      if (key === '_meta' || !entry || typeof entry !== 'object') return;
      if ('value' in entry && entry.value !== undefined) {
        next[key] = entry.value as string | boolean | number;
      } else if (SECRET_KEYS.includes(key)) {
        next[key] = '';
      }
    });
    setForm(next);
  };

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const provider = String(form.ai_provider ?? snapshot?._meta?.provider ?? 'openai');

  const setField = (key: string, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSuccess(null);
    setTestResult(null);
  };

  const scalarSource = (key: string): SettingSource | undefined => {
    const entry = snapshot?.[key] as ScalarSetting | undefined;
    return entry?.source;
  };

  const secretMeta = (key: string): SecretSetting => (snapshot?.[key] as SecretSetting) ?? {};

  const buildUpdates = (): Record<string, unknown> => {
    const updates: Record<string, unknown> = {};
    Object.entries(form).forEach(([key, value]) => {
      if (SECRET_KEYS.includes(key)) {
        if (typeof value === 'string' && value.trim() !== '') {
          updates[key] = value.trim();
        }
        return;
      }
      updates[key] = value;
    });
    return updates;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const data = await apiService.updateAdminAiSettings(buildUpdates());
      setSnapshot(data);
      initializeForm(data);
      setSuccess('AI settings saved.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const result = await apiService.testAdminAiConnection();
      setTestResult({
        success: Boolean(result.success),
        message: result.message || (result.success ? 'Connection successful' : 'Connection failed'),
      });
    } catch (err: unknown) {
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleClearSecret = (key: string) => {
    setPendingClearKey(key);
  };

  const confirmClearSecret = async () => {
    const key = pendingClearKey;
    if (!key) return;
    try {
      setClearingKey(key);
      setError(null);
      const data = await apiService.clearAdminAiSecret(key);
      setSnapshot(data);
      initializeForm(data);
      setSuccess('Secret override cleared.');
      showToast('Secret override cleared.', 'success');
      setPendingClearKey(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to clear secret');
      showToast(err instanceof Error ? err.message : 'Unable to clear secret.', 'error');
    } finally {
      setClearingKey(null);
    }
  };

  const meta = snapshot?._meta;
  const tableReady = meta?.table_ready ?? false;

  const providerSection = useMemo(() => {
    switch (provider) {
      case 'anthropic':
        return (
          <>
            <SecretField
              label="Anthropic API key"
              {...secretMeta('anthropic_api_key')}
              value={String(form.anthropic_api_key ?? '')}
              onChange={(v) => setField('anthropic_api_key', v)}
              onClear={() => handleClearSecret('anthropic_api_key')}
              clearing={clearingKey === 'anthropic_api_key'}
            />
            <Field label="Model" value={String(form.anthropic_model ?? '')} onChange={(v) => setField('anthropic_model', v)} source={scalarSource('anthropic_model')} />
            <Field label="Max tokens" type="number" value={Number(form.anthropic_max_tokens ?? 4000)} onChange={(v) => setField('anthropic_max_tokens', Number(v))} source={scalarSource('anthropic_max_tokens')} />
            <Field label="Rarity max tokens" type="number" value={Number(form.anthropic_rarity_max_tokens ?? 2500)} onChange={(v) => setField('anthropic_rarity_max_tokens', Number(v))} source={scalarSource('anthropic_rarity_max_tokens')} />
          </>
        );
      case 'cohere':
        return (
          <>
            <SecretField
              label="Cohere API key"
              {...secretMeta('cohere_api_key')}
              value={String(form.cohere_api_key ?? '')}
              onChange={(v) => setField('cohere_api_key', v)}
              onClear={() => handleClearSecret('cohere_api_key')}
              clearing={clearingKey === 'cohere_api_key'}
            />
            <Field label="Model" value={String(form.cohere_model ?? '')} onChange={(v) => setField('cohere_model', v)} source={scalarSource('cohere_model')} />
            <Field label="Max tokens" type="number" value={Number(form.cohere_max_tokens ?? 4000)} onChange={(v) => setField('cohere_max_tokens', Number(v))} source={scalarSource('cohere_max_tokens')} />
            <Field label="Temperature" type="number" value={Number(form.cohere_temperature ?? 0.1)} onChange={(v) => setField('cohere_temperature', Number(v))} source={scalarSource('cohere_temperature')} />
          </>
        );
      case 'local':
        return (
          <>
            <Field label="Endpoint URL" value={String(form.local_ai_endpoint ?? '')} onChange={(v) => setField('local_ai_endpoint', v)} source={scalarSource('local_ai_endpoint')} />
            <Field label="Model" value={String(form.local_ai_model ?? '')} onChange={(v) => setField('local_ai_model', v)} source={scalarSource('local_ai_model')} />
            <SecretField
              label="API key (optional)"
              {...secretMeta('local_ai_api_key')}
              value={String(form.local_ai_api_key ?? '')}
              onChange={(v) => setField('local_ai_api_key', v)}
              onClear={() => handleClearSecret('local_ai_api_key')}
              clearing={clearingKey === 'local_ai_api_key'}
            />
            <Field label="Max tokens" type="number" value={Number(form.local_ai_max_tokens ?? 4000)} onChange={(v) => setField('local_ai_max_tokens', Number(v))} source={scalarSource('local_ai_max_tokens')} />
            <Field label="Temperature" type="number" value={Number(form.local_ai_temperature ?? 0.1)} onChange={(v) => setField('local_ai_temperature', Number(v))} source={scalarSource('local_ai_temperature')} />
          </>
        );
      case 'openai':
        return (
          <>
            <SecretField
              label="OpenAI API key"
              {...secretMeta('openai_api_key')}
              value={String(form.openai_api_key ?? '')}
              onChange={(v) => setField('openai_api_key', v)}
              onClear={() => handleClearSecret('openai_api_key')}
              clearing={clearingKey === 'openai_api_key'}
            />
            <Field label="Model" value={String(form.openai_model ?? '')} onChange={(v) => setField('openai_model', v)} source={scalarSource('openai_model')} />
            <Field label="Max tokens" type="number" value={Number(form.openai_max_tokens ?? 9000)} onChange={(v) => setField('openai_max_tokens', Number(v))} source={scalarSource('openai_max_tokens')} />
            <Field label="Temperature" type="number" value={Number(form.openai_temperature ?? 0.1)} onChange={(v) => setField('openai_temperature', Number(v))} source={scalarSource('openai_temperature')} />
            <Field label="Rarity max tokens" type="number" value={Number(form.openai_rarity_max_tokens ?? 2500)} onChange={(v) => setField('openai_rarity_max_tokens', Number(v))} source={scalarSource('openai_rarity_max_tokens')} />
          </>
        );
      default:
        return (
          <p className="text-sm text-gray-400">
            Rule-based mode does not call external LLM APIs. Import parsing and assessments use heuristics only.
          </p>
        );
    }
  // Rebuild when provider/form change; helpers close over latest snapshot intentionally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, form, snapshot, clearingKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <ConfirmDialog
        isOpen={Boolean(pendingClearKey)}
        title="Clear secret override?"
        description="Remove the database override for this key. The app will fall back to .env if set."
        confirmLabel="Clear override"
        cancelLabel="Cancel"
        destructive
        loading={Boolean(clearingKey)}
        loadingLabel="Clearing…"
        onCancel={() => setPendingClearKey(null)}
        onConfirm={() => void confirmClearSecret()}
      />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <PageHeader eyebrow="Admin" title="AI settings" description="Provider keys and model configuration. Database values override .env when set." />
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleTest}
            disabled={testing || !form.ai_enabled}
            className="px-4 py-2 rounded-lg border border-white/[0.10] text-gray-200 hover:bg-white/[0.06] disabled:opacity-50 text-sm"
          >
            {testing ? 'Testing…' : 'Test connection'}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !tableReady}
            className="px-4 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 text-sm font-medium"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {!tableReady && (
        <div className="rounded-lg border border-amber-700 bg-amber-900/30 px-4 py-3 text-amber-100 text-sm">
          The <code className="text-amber-200">platform_settings</code> table is missing. Run the migration{' '}
          <code className="text-amber-200">20260521_platform_settings.sql</code> before saving overrides.
          Settings still read from <code className="text-amber-200">.env</code> until then.
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-900/30 px-4 py-3 text-red-200 text-sm">{error}</div>
      )}
      {success && (
        <div className="rounded-lg border border-green-800 bg-green-900/30 px-4 py-3 text-green-200 text-sm">{success}</div>
      )}
      {testResult && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            testResult.success
              ? 'border-green-800 bg-green-900/30 text-green-200'
              : 'border-red-800 bg-red-900/30 text-red-200'
          }`}
        >
          {testResult.message}
        </div>
      )}

      {meta && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-[#141414] rounded-lg p-4 border border-white/[0.08]">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Active provider</p>
            <p className="text-white font-medium mt-1 capitalize">{meta.provider}</p>
          </div>
          <div className="bg-[#141414] rounded-lg p-4 border border-white/[0.08]">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Effective model</p>
            <p className="text-white font-medium mt-1 truncate">{meta.effective_model || '-'}</p>
          </div>
          <div className="bg-[#141414] rounded-lg p-4 border border-white/[0.08]">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Provider ready</p>
            <p className={`font-medium mt-1 ${meta.ai_configured ? 'text-green-400' : 'text-amber-400'}`}>
              {meta.ai_configured ? 'Yes' : 'Not configured'}
            </p>
          </div>
        </div>
      )}

      <section className="bg-[#141414] rounded-xl border border-white/[0.08] p-5 space-y-1">
        <h2 className="text-lg font-semibold text-white mb-3">General</h2>
        <Toggle
          label="AI enabled"
          description="Master switch for all AI features"
          checked={Boolean(form.ai_enabled)}
          onChange={(v) => setField('ai_enabled', v)}
          source={scalarSource('ai_enabled')}
        />
        <div className="py-3 border-b border-white/[0.08]">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-white">Provider</span>
            {scalarSource('ai_provider') && sourceBadge(scalarSource('ai_provider'))}
          </div>
          <CustomDropdown
            value={provider}
            onChange={(v) => setField('ai_provider', v)}
            options={PROVIDERS}
            placeholder="Select provider"
          />
        </div>
        <Toggle
          label="Enable fallback parsing"
          description="Use rule-based parsing when AI fails"
          checked={Boolean(form.ai_enable_fallback)}
          onChange={(v) => setField('ai_enable_fallback', v)}
          source={scalarSource('ai_enable_fallback')}
        />
        <Toggle
          label="Import AI"
          description="Use AI for modification import analysis"
          checked={Boolean(form.ai_import_enabled)}
          onChange={(v) => setField('ai_import_enabled', v)}
          source={scalarSource('ai_import_enabled')}
        />
        <Toggle
          label="Rarity / collectibility AI"
          description="Use AI for vehicle collectibility assessments"
          checked={Boolean(form.ai_rarity_enabled)}
          onChange={(v) => setField('ai_rarity_enabled', v)}
          source={scalarSource('ai_rarity_enabled')}
        />
        <Toggle
          label="Cache AI results"
          checked={Boolean(form.ai_cache_results)}
          onChange={(v) => setField('ai_cache_results', v)}
          source={scalarSource('ai_cache_results')}
        />
      </section>

      <section className="bg-[#141414] rounded-xl border border-white/[0.08] p-5 space-y-4">
        <h2 className="text-lg font-semibold text-white">Provider configuration</h2>
        {providerSection}
      </section>

      <section className="bg-[#141414] rounded-xl border border-white/[0.08] p-5 space-y-4">
        <h2 className="text-lg font-semibold text-white">Rate limits & assessment</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="AI rate limit (requests)"
            type="number"
            value={Number(form.ai_rate_limit ?? 60)}
            onChange={(v) => setField('ai_rate_limit', Number(v))}
            source={scalarSource('ai_rate_limit')}
          />
          <Field
            label="Rate limit window (seconds)"
            type="number"
            value={Number(form.ai_rate_limit_window ?? 3600)}
            onChange={(v) => setField('ai_rate_limit_window', Number(v))}
            source={scalarSource('ai_rate_limit_window')}
          />
          <Field
            label="Assessment AI timeout (seconds)"
            type="number"
            value={Number(form.assessment_ai_timeout_seconds ?? 90)}
            onChange={(v) => setField('assessment_ai_timeout_seconds', Number(v))}
            source={scalarSource('assessment_ai_timeout_seconds')}
          />
          <Field
            label="Default rarity max tokens"
            type="number"
            value={Number(form.ai_rarity_max_tokens ?? 2500)}
            onChange={(v) => setField('ai_rarity_max_tokens', Number(v))}
            source={scalarSource('ai_rarity_max_tokens')}
            hint="Fallback when provider-specific rarity token limit is not set"
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-medium text-white">Assessment mode</span>
            {scalarSource('assessment_mode') && sourceBadge(scalarSource('assessment_mode'))}
          </div>
          <CustomDropdown
            value={String(form.assessment_mode ?? 'inline')}
            onChange={(v) => setField('assessment_mode', v)}
            options={ASSESSMENT_MODES}
            placeholder="Assessment mode"
          />
        </div>
      </section>
    </div>
  );
};
